'use client';

import Accordion from '@/components/Accordion';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useLocale } from 'next-intl';
import FrenchContent from './FrenchContent';
import GermanContent from './GermanContent';
import Image from 'next/image';

// 定义文本对象接口
interface TextObject {
  description: string | string[];
  boldParts?: string[];
  links?: Array<{ text: string; url: string }>;
  [key: string]: any;
}

// 格式化文本函数，处理加粗文本和链接
const formatText = (textObj: TextObject) => {
  if (!textObj) {
    return '';
  }

  let { description } = textObj;
  const { boldParts = [] } = textObj;

  // 处理数组类型的描述
  if (Array.isArray(description)) {
    description = description.join('<br/>');
  }

  // 处理加粗文本
  let formattedText = description;
  if (Array.isArray(boldParts)) {
    boldParts.forEach((bold) => {
      if (typeof bold === 'string') {
        const plainRegex = new RegExp(bold, 'g');
        formattedText = formattedText.replace(
          plainRegex,
          `<span class="font-bb text-primary-black">${bold}</span>`,
        );
      }
    });
  }

  // 处理链接
  if (textObj.links) {
    textObj.links.forEach((link: { text: string; url: string }) => {
      formattedText = formattedText.replace(
        link.text,
        `<a href="${link.url}" class="text-[#463ae8] underline cursor-pointer">${link.text}</a>`,
      );
    });
  }

  return formattedText;
};

export default function ReturnRefundClient({
  returnRefundData,
  isMobile,
}: {
  returnRefundData: any;
  isMobile: boolean;
}) {
  const locale = useLocale();

  // 如果是德语或法语，显示静态内容
  // ！de,fr老服务就是直接静态页面做的，不是通过api做的，后续有变更再说
  if (locale === 'de') {
    return <GermanContent />;
  }

  if (locale === 'fr') {
    return <FrenchContent />;
  }

  const renderContent = (item: any) => {
    return (
      <>
        <div dangerouslySetInnerHTML={{ __html: formatText(item) }} />
        {item.listInfo && (
          <ul style={{ listStyleType: 'disc', paddingLeft: '12px' }}>
            {item.listInfo.map((condition: string, index: number) => (
              <li key={index} className="">
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatText({
                      description: condition,
                      boldParts: item.boldParts,
                    }),
                  }}
                />
              </li>
            ))}
          </ul>
        )}
        {item.type === 'image' && (
          <Image className="w-full object-contain mt-[8px]" src={item.imgUrl} alt="" />
        )}
      </>
    );
  };

  const items = returnRefundData.questions.map((item: any, index: number) => ({
    key: item.id,
    title: `${index + 1}. ${item.question}`,
    content: renderContent(item),
  }));

  return (
    <div
      className={`${isMobile ? 'px-[14px] pb-[70px]' : 'px-[220px] py-[72px] pb-[100px]'} text-center relative`}
    >
      {isMobile && (
        <div className="relative pt-[56px] mb-[12px]">
          <h1 className="font-be text-[24px] text-primary-black">{returnRefundData.title}</h1>
        </div>
      )}

      {isMobile && (
        <div className="absolute left-[14px] top-[10px] z-[99] text-primary-black text-[12px] font-ir">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Returns & Refunds' }]} />
        </div>
      )}
      {!isMobile && (
        <div className="mb-[60px]">
          <h1 className="font-be text-[40px] text-primary-black">{returnRefundData.title}</h1>
        </div>
      )}

      <div className="faq-container">
        <Accordion
          items={items}
          accordion={false}
          borderless
          summaryStyle={{
            fontFamily: 'var(--font-bricolage-bold)',
            fontSize: isMobile ? '14px' : '22px',
          }}
          detailsStyle={{
            textAlign: 'left',
            fontFamily: 'var(--font-ir)',
            fontSize: isMobile ? '13px' : '16px',
            lineHeight: isMobile ? '18px' : '24px',
          }}
          sx={{
            borderTop: '1px solid #d4d4d4 !important',
            overflow: 'hidden',
            borderRadius: '0px !important',
            paddingTop: '4px !important',
            marginBottom: '4px !important',
          }}
        />
      </div>
    </div>
  );
}
